/**
 * @file recent-memory.ts
 * @description Память «Последние» общих полей конструктора (phase19.16,
 * партия SB120 — свобода выбора; uiux-sb120-fields §3, §4): на устройстве,
 * отдельно по магазину.
 *
 * Один хелпер `createRecentMemory` на все виды — новый вид = запись, не
 * копия кода (реестр вместо if-каскадов):
 *   - цвета окна «Свой цвет» — `commora.recent.colors.<shopId>`, до 8;
 *   - шрифты окна «Шрифт элемента» — `commora.recent.fonts.<shopId>`, до 6.
 * Без магазина (платформенный кабинет, тесты) — ключ без `.<shopId>`.
 * Значение — JSON-массив строк, новое — в начало, повтор поднимается наверх,
 * а не дублируется. Все чтения и записи — в try/catch (как
 * `settings-groups/memory.ts`): хранилище может быть недоступно — тогда окно
 * просто не помнит.
 */

import { isElementFontKey, SITE_COLOR_PATTERN } from "@chaiberi/shared";

/** Вид памяти: имя в ключе, предел, проверка и приведение значения. */
interface RecentKind {
  /** Часть ключа: `commora.recent.<name>`. */
  name: string;
  limit: number;
  isValid: (value: string) => boolean;
  /** Приведение к одному виду, чтобы повтор не дублировался. */
  normalize: (value: string) => string;
}

export interface RecentMemory {
  limit: number;
  key: (shopId: string | null | undefined) => string;
  read: (shopId: string | null | undefined) => string[];
  /** Добавить в начало (без повторов, не больше предела) и вернуть новый список. */
  push: (shopId: string | null | undefined, value: string) => string[];
}

/** Память «Последние» одного вида. */
export function createRecentMemory(kind: RecentKind): RecentMemory {
  const key = (shopId: string | null | undefined): string =>
    shopId ? `commora.recent.${kind.name}.${shopId}` : `commora.recent.${kind.name}`;

  const read = (shopId: string | null | undefined): string[] => {
    try {
      const raw = window.localStorage.getItem(key(shopId));
      if (!raw) return [];
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      const valid = parsed.filter((x): x is string => typeof x === "string" && kind.isValid(x)).map(kind.normalize);
      return [...new Set(valid)].slice(0, kind.limit);
    } catch {
      return [];
    }
  };

  const push = (shopId: string | null | undefined, value: string): string[] => {
    if (!kind.isValid(value)) return read(shopId);
    const item = kind.normalize(value);
    const next = [item, ...read(shopId).filter((v) => v !== item)].slice(0, kind.limit);
    try {
      window.localStorage.setItem(key(shopId), JSON.stringify(next));
    } catch {
      // Хранилище недоступно — список живёт до закрытия окна.
    }
    return next;
  };

  return { limit: kind.limit, key, read, push };
}

// ── Цвета ──

const COLORS = createRecentMemory({
  name: "colors",
  limit: 8,
  isValid: (hex) => SITE_COLOR_PATTERN.test(hex),
  // Нижний регистр — `#2F54EB` и `#2f54eb` один цвет.
  normalize: (hex) => hex.trim().toLowerCase(),
});

/** Сколько цветов помнить. */
export const RECENT_COLORS_LIMIT = COLORS.limit;
/** Ключ памяти: `commora.recent.colors.<shopId>` или общий без магазина. */
export const recentColorsKey = COLORS.key;
/** Последние цвета магазина; испорченная или недоступная память — пусто. */
export const readRecentColors = COLORS.read;
/** Добавить цвет в начало (без повторов, не больше 8) и вернуть новый список. */
export const pushRecentColor = COLORS.push;

// ── Шрифты ──

/**
 * Ключ шрифта хранится как есть: ключи каталога и cuid своих шрифтов — уже
 * в нижнем регистре. Есть ли шрифт в реестре магазина — решает окно при
 * показе: удалённый свой шрифт просто не показывается.
 */
const FONTS = createRecentMemory({ name: "fonts", limit: 6, isValid: isElementFontKey, normalize: (key) => key });

/** Сколько шрифтов помнить. */
export const RECENT_FONTS_LIMIT = FONTS.limit;
/** Ключ памяти: `commora.recent.fonts.<shopId>` или общий без магазина. */
export const recentFontsKey = FONTS.key;
/** Последние шрифты магазина (ключи); испорченная память — пусто. */
export const readRecentFonts = FONTS.read;
/** Добавить шрифт в начало (без повторов, не больше 6) и вернуть новый список. */
export const pushRecentFont = FONTS.push;
